/**
 * Membership Toggle Component
 * Handles adding/removing Club Loom Loom membership from cart via checkbox toggle
 */

if (!customElements.get('membership-toggle')) {
  class MembershipToggle extends HTMLElement {
    constructor() {
      super();
      this.membershipProductType = 'Subscription';
      this.boundHandleToggle = this.handleToggle.bind(this);
    }

    connectedCallback() {
      // Query elements fresh each time the component is connected to DOM
      // This is critical because replaceWith creates new elements
      this.checkbox = this.querySelector('[data-membership-toggle]');
      this.label = this.querySelector('.cart-footer-compact__toggle-label') ||
                   this.querySelector('.membership-upsell__toggle-label');

      // Determine context (cart page vs cart drawer)
      this.context = this.dataset.context || 'CartDrawer-Form';

      if (this.checkbox) {
        this.membershipUrl = this.checkbox.dataset.membershipUrl || '/community/membership';
        this.variantId = this.checkbox.dataset.variantId;
        this.sellingPlanId = this.checkbox.dataset.sellingPlanId;
        this.lineKey = this.checkbox.dataset.lineKey;

        // Remove any existing listener before adding (prevents duplicates)
        this.checkbox.removeEventListener('change', this.boundHandleToggle);
        this.checkbox.addEventListener('change', this.boundHandleToggle);
      }
    }

    disconnectedCallback() {
      // Clean up event listener when element is removed
      if (this.checkbox) {
        this.checkbox.removeEventListener('change', this.boundHandleToggle);
      }
    }

    async handleToggle(event) {
      const isChecked = event.target.checked;

      // Add loading state
      this.setLoading(true);

      try {
        if (isChecked) {
          // Check if membership already exists before adding
          const existingMembership = await this.getMembershipLineKeys();
          if (existingMembership.length > 0) {
            // Already has membership, just refresh to sync state
            this.refreshCart();
            return;
          }

          // Add membership to cart
          if (this.variantId) {
            await this.addMembershipToCart(this.variantId);
            this.refreshCart();
          } else {
            // Fallback: redirect to membership page
            window.location.href = this.membershipUrl;
          }
        } else {
          // Remove ALL membership instances from cart
          const lineKeys = await this.getMembershipLineKeys();
          if (lineKeys.length > 0) {
            await this.removeAllMemberships(lineKeys);
            this.refreshCart();
          } else {
            console.error('Could not find membership line keys');
            event.target.checked = true;
            this.setLoading(false);
          }
        }
      } catch (error) {
        console.error('Membership toggle error:', error);
        // Revert checkbox state on error
        event.target.checked = !isChecked;
        this.setLoading(false);
      }
    }

    /**
     * Get ALL membership line keys from cart API
     */
    async getMembershipLineKeys() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const membershipItems = cart.items.filter(item => item.product_type === this.membershipProductType);
        return membershipItems.map(item => item.key);
      } catch (e) {
        console.error('Error getting membership line keys:', e);
        return [];
      }
    }

    /**
     * Add membership to cart with optional selling plan (for subscriptions)
     */
    async addMembershipToCart(variantId) {
      const item = {
        id: variantId,
        quantity: 1
      };

      // Add selling plan if available (required for Inveterate subscriptions)
      if (this.sellingPlanId) {
        item.selling_plan = this.sellingPlanId;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [item]
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Cart add error:', errorData);
        throw new Error(errorData.description || 'Failed to add membership to cart');
      }

      return response.json();
    }

    /**
     * Remove ALL membership instances from cart
     */
    async removeAllMemberships(lineKeys) {
      // Build updates object to set all membership quantities to 0
      const updates = {};
      for (const key of lineKeys) {
        updates[key] = 0;
      }

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Cart update error:', errorData);
        throw new Error('Failed to remove memberships from cart');
      }

      return response.json();
    }

    /**
     * Refresh the cart UI using Dawn's pattern (section_id + replaceWith)
     * Always refreshes BOTH cart drawer and cart page to keep them in sync
     */
    refreshCart() {
      // Always refresh both to keep them in sync
      this.refreshCartDrawer();
      this.refreshCartPage();

      // Always update cart icon bubble
      this.refreshCartIconBubble();
    }

    /**
     * Refresh cart drawer sections
     */
    refreshCartDrawer() {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then(response => response.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');

          // Use replaceWith for proper element replacement (Dawn pattern from cart.js)
          const selectors = ['cart-drawer-items', '.drawer__footer'];
          for (const selector of selectors) {
            const target = document.querySelector(selector);
            const source = doc.querySelector(selector);
            if (target && source) {
              target.replaceWith(source);
            }
          }
        })
        .catch(() => window.location.reload());
    }

    /**
     * Refresh cart page sections (items + footer)
     */
    refreshCartPage() {
      // Refresh main-cart-items section
      const cartItemsSection = document.getElementById('main-cart-items');
      if (cartItemsSection) {
        const sectionId = cartItemsSection.dataset.id || 'main-cart-items';
        fetch(`${routes.cart_url}?section_id=${sectionId}`)
          .then(response => response.text())
          .then(html => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newContent = doc.querySelector('.js-contents');
            const target = cartItemsSection.querySelector('.js-contents');
            if (target && newContent) {
              target.innerHTML = newContent.innerHTML;
            }
          })
          .catch(() => {});
      }

      // Refresh main-cart-footer section
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) {
        const sectionId = cartFooter.dataset.id || 'main-cart-footer';
        fetch(`${routes.cart_url}?section_id=${sectionId}`)
          .then(response => response.text())
          .then(html => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newFooter = doc.getElementById('main-cart-footer');
            if (newFooter) {
              cartFooter.innerHTML = newFooter.innerHTML;
            }
          })
          .catch(() => window.location.reload());
      }
    }

    /**
     * Refresh cart icon bubble
     */
    refreshCartIconBubble() {
      fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
        .then(response => response.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const bubble = document.getElementById('cart-icon-bubble');
          const newBubble = doc.querySelector('.shopify-section');
          if (bubble && newBubble) {
            bubble.innerHTML = newBubble.innerHTML;
          }
        })
        .catch(() => {});
    }

    setLoading(isLoading) {
      if (this.label) {
        this.label.classList.toggle('is-loading', isLoading);
      }
      if (this.checkbox) {
        this.checkbox.disabled = isLoading;
      }
    }
  }

  customElements.define('membership-toggle', MembershipToggle);
}
